import classNames from 'classnames';
import Head from 'next/head';
import React from 'react';

import BackTo from '@jpp/organisms/BackTo';
import PDPHero from '@jpp/organisms/PDP/PDPHero';
import PDPInformation from '@jpp/organisms/PDP/PDPInformation';
import UpSell from '@jpp/organisms/UpSell';
import ErrorTemplate from '@jpp/templates/Error';
import {
  trackGooglePDP,
  trackProductImpression,
} from 'common/tracking/product';
import { EPageType } from 'common/typings/enums';
import { EProductStock, EProductType } from 'common/typings/enums/product';
import { IProductTemplateProps } from 'common/typings/types';
import { NOT_FOUND_STATUS_CODE } from 'common/utils/constants';

import './Product.scss';

const ROOT_CLASSNAME = 'Product';

const Product: React.FunctionComponent<IProductTemplateProps> = ({ page }) => {
  const acf = page.acf || page.acf_fields;

  const {
    product_description,
    beer_info,
    upsell_products,
    default_product,
    default_product_range,
  } = acf || {};
  const { post_title, pdp_type } = page || {};
  const shouldRenderBeerInfo =
    pdp_type === EProductType.Simple ||
    pdp_type === EProductType.Special ||
    pdp_type === EProductType.SpecialBottle ||
    pdp_type === EProductType.Bundle;
  const title = post_title || page.title;
  const productId = default_product?.id || default_product?.ID;
  const price = page.sale_price || page.regular_price || page.price;
  const stockStatus = page?.stock_status || default_product?.stock_status;
  const isInStock = stockStatus === EProductStock.InStock;

  const ogImage = page.featured_image?.url;

  React.useEffect(() => {
    if (page && productId && default_product_range?.name) {
      trackProductImpression(
        title,
        productId,
        page.sku,
        price,
        default_product_range.name,
        page.slug,
        (page?.image || page?.featured_image)?.url
      );

      trackGooglePDP([page]);
    }
  }, [default_product_range?.name, page, price, productId, title]);

  if (!acf || !page) {
    return <ErrorTemplate statusCode={NOT_FOUND_STATUS_CODE} />;
  }

  return (
    <>
      {ogImage && (
        <Head>
          <meta key="og:image" property="og:image" content={ogImage} />
        </Head>
      )}

      <div className={ROOT_CLASSNAME}>
        <BackTo
          href={`/${EPageType.Shop}`}
          label="the beer shop"
          className={`${ROOT_CLASSNAME}__back-to`}
        />

        <PDPHero pdp={page} className={`${ROOT_CLASSNAME}__hero`} />

        {(product_description || beer_info) && (
          <PDPInformation
            pdpType={pdp_type!}
            content={product_description!}
            beerInfo={beer_info!}
            shouldRenderBeerInfo={shouldRenderBeerInfo}
            className={classNames(`${ROOT_CLASSNAME}__information`, {
              [`${ROOT_CLASSNAME}__information--product-oos`]: !isInStock,
            })}
          />
        )}

        <UpSell
          products={upsell_products}
          className={classNames(`${ROOT_CLASSNAME}__upsell`, {
            [`${ROOT_CLASSNAME}__upsell--product-oos`]: !isInStock,
          })}
        />
      </div>
    </>
  );
};

export default Product;
